import { useState } from 'react';
import { getPlanPriceId, openCheckout } from '../../constants/pricing';
import './PaigeXReviewLumenSection.css';

const STEPS = [
  {
    num: '01',
    label: 'Profile Optimization',
    title: 'Your Google Business Profile, fully optimized',
    desc: 'Paige rewrites your services, descriptions, and attributes to match what\'s already ranking across thousands of profiles — no SEO knowledge required on your end.',
    tags: ['Services & attributes', 'Auto-published posts', 'Photo refresh'],
    stat: '72%',
    statLabel: 'Average optimization score after setup',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 2 15 9l7 1-5 5 1.5 7L12 18.5 5.5 22 7 15 2 10l7-1z" />
      </svg>
    ),
  },
  {
    num: '02',
    label: 'Review Generation',
    title: 'Keyword-rich reviews, coming in on autopilot',
    desc: 'On top of Review Lumen\'s own review tools, Paige follows up with customers who forget, and can auto-reply to every review that comes in — so your profile stays active without you touching it.',
    tags: ['Auto follow-up', 'Auto-reply', 'Set & forget'],
    stat: '4.8',
    statLabel: 'Typical review rating after 90 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    ),
  },
  {
    num: '03',
    label: 'Citation Building',
    title: 'Listed everywhere Google (and AI) checks',
    desc: 'One click sends your business info to every directory Google cross-references, plus the ones AI assistants pull from — Apple Maps, Bing, Waze, ChatGPT and more.',
    tags: ['Directory sync', 'AI-source listings', 'One-click push'],
    stat: '50+',
    statLabel: 'Directories kept in sync',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
  },
  {
    num: '04',
    label: 'Social Publishing',
    title: 'Every Google post, mirrored to social',
    desc: 'Google favors businesses that stay active across the web. Whatever Paige creates for your profile gets pushed to Facebook, Instagram, and LinkedIn automatically.',
    tags: ['Facebook', 'Instagram', 'LinkedIn'],
    stat: '3x',
    statLabel: 'Channels updated per post',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M22 2 11 13" />
        <path d="M22 2 15 22l-4-9-9-4 20-7z" />
      </svg>
    ),
  },
  {
    num: '05',
    label: 'AI Search Visibility',
    title: 'Findable on ChatGPT, Gemini & Perplexity',
    desc: 'Local search isn\'t just Google Maps anymore. Paige optimizes for AI-mode results too, so you show up wherever people are asking for a business like yours.',
    tags: ['ChatGPT', 'Gemini', 'Perplexity'],
    stat: '5+',
    statLabel: 'AI platforms covered',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    num: '06',
    label: 'Website SEO',
    title: 'Website copy that matches your Google profile',
    desc: 'Google cross-references your website against your profile to judge how legitimate your business is. Paige reviews your site and hands you ready-to-add schema and FAQ code that lifts both your website and your profile at once.',
    tags: ['Schema markup', 'FAQ code', 'Instant suggestions'],
    stat: '80%',
    statLabel: 'Average site SEO score after sync',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M8 4 3 12l5 8M16 4l5 8-5 8M13 3l-2 18" />
      </svg>
    ),
  },
  {
    num: '07',
    label: 'YouTube Publishing',
    title: 'YouTube videos, made and published for you',
    desc: 'YouTube is the second-largest search engine on earth, and most local businesses never touch it. Paige turns your own photos into videos, publishes them to your channel, and links back to your profile for extra authority.',
    tags: ['Auto-generated videos', 'YouTube channel', 'Backlink to profile'],
    stat: '#2',
    statLabel: 'Search engine most businesses ignore',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="4" />
        <path d="M10 9.5v5l4.5-2.5z" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
] as const;

export function PaigeXReviewLumenSection() {
  const [active, setActive] = useState(0);

  const goTo = (index: number) =>
    setActive(Math.max(0, Math.min(STEPS.length - 1, index)));

  const handleProCheckout = () => {
    openCheckout(getPlanPriceId('pro'));
  };

  return (
    <section
      id="review-lumen-x-paige"
      className="pxl-section"
      aria-labelledby="paige-partnership-heading"
    >
      <div className="pxl-wrap">
        <span className="pxl-eyebrow">
          <span className="dot" />
          Paige × Review Lumen
        </span>
        <h2 id="paige-partnership-heading" className="pxl-head">
          One <span>Google Business Profile</span>,
          <br />
          fully automated by Paige.
        </h2>
        <p className="pxl-sub">
          We&apos;ve partnered with Paige to give Review Lumen clients an AI profile
          manager on top of your reviews — five things running quietly in the
          background so your listing keeps climbing.
        </p>

        <div
          className="pxl-rail"
          style={{ gridTemplateColumns: `repeat(${STEPS.length}, 1fr)` }}
          role="tablist"
          aria-label="Paige partnership features"
        >
          <div className="pxl-rail-track" aria-hidden="true">
            <div
              className="pxl-rail-fill"
              style={{ width: `${(active / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
          {STEPS.map((step, index) => (
            <button
              key={step.num}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-controls={`paige-panel-${step.num}`}
              className={`pxl-stop${index === active ? ' active' : ''}`}
              onClick={() => goTo(index)}
            >
              <span className="pxl-stop-dot">{step.num}</span>
              <span className="pxl-stop-label">{step.label}</span>
            </button>
          ))}
        </div>

        <div className="pxl-stage">
          <div
            className="pxl-track"
            style={{
              width: `${STEPS.length * 100}%`,
              transform: `translateX(-${active * (100 / STEPS.length)}%)`,
            }}
          >
            {STEPS.map((step, index) => (
              <div
                className="pxl-panel"
                key={step.num}
                id={`paige-panel-${step.num}`}
                role="tabpanel"
                aria-hidden={index !== active}
                style={{ width: `${100 / STEPS.length}%` }}
              >
                <div className="pxl-copy">
                  <div className="pxl-num">STEP {step.num}</div>
                  <h3 className="pxl-title">{step.title}</h3>
                  <p className="pxl-desc">{step.desc}</p>
                  <div className="pxl-tags">
                    {step.tags.map((tag) => (
                      <span className="pxl-tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="pxl-cta"
                    onClick={handleProCheckout}
                  >
                    Add Paige to my plan
                  </button>
                </div>
                <div className="pxl-visual">
                  <div className="pxl-card">
                    <div className="pxl-card-icon">{step.icon}</div>
                    <div className="pxl-card-stat">{step.stat}</div>
                    <div className="pxl-card-stat-label">{step.statLabel}</div>
                    <div className="pxl-card-bar">
                      <div
                        className="pxl-card-bar-fill"
                        style={{ width: `${65 + index * 7}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pxl-arrows">
          <button
            type="button"
            className="pxl-arrow-btn"
            aria-label="Previous step"
            onClick={() => goTo(active - 1)}
            disabled={active === 0}
          >
            &#8592;
          </button>
          <button
            type="button"
            className="pxl-arrow-btn"
            aria-label="Next step"
            onClick={() => goTo(active + 1)}
            disabled={active === STEPS.length - 1}
          >
            &#8594;
          </button>
        </div>
      </div>
    </section>
  );
}
